import { IMerchantDetails } from 'api/merchants/types';
import { ISingleRuleset } from 'api/routingRuleset/types';
import {
  EMPTY_METADATA_PAYLOAD,
  normalizeGroupEditMetadataPayload,
  normalizeMetadataPayload,
} from 'components/Metadata/helpers';
import { CREDIT_CARD } from 'constants/common';
import { INullable } from 'utils/types';

import { nullableMetadata } from './constants';
import { IRoutingRulesetForm, IStats } from './types';

export {
  EMPTY_METADATA_PAYLOAD,
  normalizeGroupEditMetadataPayload,
  normalizeMetadataPayload,
};

export const commissionPctValidator = (newValue: string) => {
  if (!newValue) {
    return true;
  }
  const isValid = /^(\d{0,2}(\.\d{0,2})?|100)?$/.test(newValue);
  if (!isValid) {
    return false;
  }
  if (newValue.length > 1 && newValue.startsWith('0') && newValue[1] !== '.') {
    return false;
  }
  const value = Number(newValue);
  return value >= 0 && value <= 100;
};

export const minCommissionValidator = (newValue: string) => {
  // Empty value is valid — treated as "no minimum commission"
  if (!newValue) {
    return true;
  }
  const isValid = /^\d+\.?\d{0,2}$/.test(newValue);
  if (!isValid) {
    return false;
  }
  if (newValue.length > 1 && newValue.startsWith('0') && newValue[1] !== '.') {
    return false;
  }
  return Number(newValue) >= 0;
};

export const adjustmentValidator = (newValue: string) => {
  const isValid = /^-?([0-9]{1,2}|100)?\.?([0-9]{1,2})?$/.test(newValue);
  if (!isValid) {
    return false;
  }
  const value = Number(newValue);
  const isGreaterThenMax = value > 100;
  const isLessThenMin = value < -99;

  return !(isGreaterThenMax || isLessThenMin);
};

interface IGetFormSectionsPermissions {
  clientStatsEnabled: boolean;
  merchant?: IMerchantDetails;
  paymentMethod?: INullable<string>;
}

export const getFormSectionsPermissions = ({
  clientStatsEnabled,
  merchant,
  paymentMethod,
}: IGetFormSectionsPermissions) => {
  return {
    creditCardSelected: paymentMethod === CREDIT_CARD,
    maxStatsEnabled: merchant?.routing?.useClientMaxStatsForRouting,
    statsEnabled: merchant?.routing?.useClientStatsForRouting,
    statsForClientEnabled:
      merchant?.routing?.useClientStatsForRouting && clientStatsEnabled,
    timerEnabled: merchant?.routing?.preMemorisedRouting,
  };
};

export const constructInitialValues = ({
  data,
  namePrefix,
  permissions,
}: {
  data: ISingleRuleset;
  namePrefix?: string;
  permissions: ReturnType<typeof getFormSectionsPermissions>;
}): IRoutingRulesetForm => {
  const {
    creditCardSelected,
    maxStatsEnabled,
    statsEnabled,
    statsForClientEnabled,
    timerEnabled,
  } = permissions;

  return {
    closedLoopEnabled: data.closedLoopEnabled,
    closedLoopTerminalLinks: data.closedLoopTerminalLinks?.length
      ? data.closedLoopTerminalLinks
      : null,
    commissionMin: data.commissionMin,
    commissionOverride: data.commissionOverride,
    commissionPct: data.commissionPct,

    enabled: data.enabled,
    name: namePrefix ? `${data.name}${namePrefix}` : data.name,

    merchantId: data.merchantTerminal.merchant?.id || null,
    merchantTerminalId: data.merchantTerminal.id,
    routingSchemaId: data.routingSchema.id,

    cashierPaymentMethodCode: data.cashierPaymentMethod?.paymentMethod || null,
    paymentMethod: data.paymentMethod,
    priority: data.priority,
    ratio: data.ratio,
    terminalLinkId: data.terminalLink?.id || null,

    bin: creditCardSelected ? data.bin : null,
    crossStats:
      data.crossStats && statsForClientEnabled
        ? {
            cumulativeStats: data.crossStats.cumulativeStats,
            leaderStats: data.crossStats.leaderStats,
            merchantGroupId: data.crossStats.merchantGroup.id,
          }
        : null,
    customer: data.customer,
    maxStats: maxStatsEnabled ? data.maxStats : null,
    paymentProviderCurrency: data.paymentProviderCurrency,
    schedule: data.schedule,
    stats: statsEnabled ? data.stats : null,
    timer: timerEnabled
      ? {
          failedAttempts: data.timerConfig?.failTimerEnabled
            ? {
                enabled: data.timerConfig.failTimerEnabled,
                recoveryPeriod: data.timerConfig.failRecoveryPeriod,
                retryCount: data.timerConfig.failRetryCount,
              }
            : {
                enabled: !!data.timerConfig?.failTimerEnabled,
                recoveryPeriod: null,
                retryCount: null,
              },
          processedAttempts: data.timerConfig?.successTimerEnabled
            ? {
                enabled: data.timerConfig.successTimerEnabled,
                recoveryPeriod: data.timerConfig.successValidityPeriod,
                retryCount: data.timerConfig.successRetryCount,
              }
            : {
                enabled: !!data.timerConfig?.successTimerEnabled,
                recoveryPeriod: null,
                retryCount: null,
              },
        }
      : null,
    transaction: {
      ...data.transaction,
      metadata: data.transaction.metadata?.map((row) => ({
        ...row,
        useTemplate: false,
      })) || [nullableMetadata],
    },
  };
};

export const isStatsEmpty = (stats: INullable<IStats>): boolean => {
  if (!stats) return true;
  return Object.values(stats).every((v) => v === null || v === undefined || v === '');
};

export const normalizeScheduleForPayload = (
  schedule: IRoutingRulesetForm['schedule'],
): IRoutingRulesetForm['schedule'] => {
  if (!schedule.enabled) {
    return { enabled: false, periodsByDays: null, timeZone: schedule.timeZone };
  }

  if (!schedule.periodsByDays) {
    return schedule;
  }

  const days = Object.entries(schedule.periodsByDays).filter(([_, value]) => !!value);

  const periodsByDays = days.length
    ? Object.fromEntries(
        days.map(([day, value]) => [
          day,
          value?.from || value?.to ? value : { from: null, to: null },
        ]),
      )
    : null;

  return { ...schedule, periodsByDays };
};

export const normalizeBinForPayload = (
  bin: IRoutingRulesetForm['bin'],
): IRoutingRulesetForm['bin'] => {
  if (!bin) {
    return null;
  }

  return { ...bin, billingInfo: !!bin.billingInfo };
};
